import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { useAsync } from '../aai/useAsync';
import type { OrbitCollection, OrbitCollectionView, WhatsAllowedData } from '../../types/aai';
import {
  Divided, EmptyState, ErrorNote, ListRow, Loading,
  PageBody, Panel, PanelHead, RouteMark, StatusChip,
} from '../../components/aa';
import { ClientHeader } from './ClientHeader';
import { ActivityView } from './ActivityView';
import { OrbitRoomPanel } from '../orbit-room/OrbitRoomPanel';
import type { ActivityLinkResolver } from './ActivityView';

/*
 * Client Context: the assigned client's own goals, sources, owners, and
 * reviewed findings, as the Solution Partner is allowed to see them.
 *
 * Every read is re-authorized against the assignment on the server, so this
 * page cannot show a company the partner is not working in. What it
 * deliberately does not show is source CONTENT or any individual's readiness:
 * seeing that a source exists and holding rights to read it are different
 * permissions, and a partner holds neither on the client's behalf. The access
 * banner states that boundary on the page's face rather than in a tooltip.
 *
 * Orbit collections are READ here. The page lists them rather than asking for
 * the current one, because `current` creates a draft when there is none and
 * opening a context page must not author anything.
 *
 * The same body serves the Owner's Company Context, the Department Head's
 * Department Context and the employee's own. What differs is the viewer, which
 * the server scopes: each of them reads a different amount through one screen
 * rather than through four.
 */

export type ContextViewer = 'partner' | 'owner' | 'department' | 'champion' | 'employee';

const isPartner = (viewer: ContextViewer): boolean => viewer === 'partner';

/* Overview is not here: it was a wrapper around the Orbit Room, which now sits
   above these tabs in full. Orbit findings is not here either: what Orbit finds
   reaches a company as an Opportunity, and Opportunities are decided on
   Projects. Owners & rules has gone the same way: it restated who approves
   what, which the screens that ask for an approval already say at the point of
   asking, beside a guidelines list that belongs with the guidelines.

   Documents is not here either: it is a screen of its own in the sidebar, for
   the Owner, because what the company's work holds is a place people go to
   rather than a tab they find while reading about something else.

   What remains each holds something no other screen does: the tools and grants
   with their expiry, and the audit trail.

   Controls is appended by the caller, not declared here. It is the Owner's
   surface, and no other viewer of this body may see it. */
const TABS = [
  { id: 'sources', label: 'Company tools' },
  { id: 'activity', label: 'Activity' },
];

const AppsSourcesTab: React.FC<{
  allowed: WhatsAllowedData;
  view: OrbitCollectionView | null;
  viewer: ContextViewer;
  before?: React.ReactNode;
}> = ({ allowed, view, viewer, before }) => (
  <div className="space-y-5">
    {before}
    <Panel>
      <PanelHead
        kicker="Sources"
        title={`${view?.sources.length || 0} source groups`}
        desc="Each one carries its owner, what kind of data it holds, and when the grant ends."
      />
      {!view && (
        <EmptyState
          title="No source groups yet"
          reason="Source groups appear here once Orbit has read something for this company. Add context from the Orbit Room on the Overview tab."
        />
      )}
      {view && (
        <Divided>
          {view.sources.map((source) => (
            <ListRow
              key={source.key}
              mark={<RouteMark kind="connection" size="md" />}
              title={source.label}
              desc={source.detail}
              meta={[
                { label: 'Owner', value: source.owner },
                { label: 'Data', value: source.data },
                { label: 'Grant ends', value: source.expiry },
              ]}
              right={
                <StatusChip
                  status={source.available ? 'confirmed' : 'needs_review'}
                  label={source.state}
                />
              }
            />
          ))}
        </Divided>
      )}
    </Panel>

    <div className="grid gap-5 lg:grid-cols-2">
      <Panel>
        <PanelHead
          kicker="Apps"
          title={`${allowed.tools_in_use.length} tools in use`}
          desc={isPartner(viewer) ? "What the client's people have told the program they use." : 'What your people have told the program they use.'}
        />
        {allowed.tools_in_use.length === 0 && (
          <EmptyState title="No tools recorded" reason="Tools appear here as people record what they use." />
        )}
        <Divided>
          {allowed.tools_in_use.map((tool) => (
            <ListRow
              key={tool.value}
              mark={<RouteMark kind="connection" size="md" />}
              title={tool.label}
              meta={[{ label: 'Users', value: `${tool.users}` }]}
            />
          ))}
        </Divided>
      </Panel>

      <Panel>
        <PanelHead kicker="Cleared tools" title="Formally approved" desc="Published and announced vendor evaluations." />
        {(allowed.approved_tools || []).length === 0 && (
          <EmptyState title="Nothing formally cleared" reason="A tool appears here once its evaluation is published and announced." />
        )}
        <Divided>
          {(allowed.approved_tools || []).map((tool) => (
            <ListRow
              key={tool.id}
              mark={<RouteMark kind="connection" size="md" />}
              title={tool.title}
              desc={tool.tools.join(' · ')}
              foot={tool.recommendation || undefined}
            />
          ))}
        </Divided>
      </Panel>
    </div>
  </div>
);

export const useLatestCollection = (clientId: string | undefined) => {
  const [view, setView] = useState<OrbitCollectionView | null>(null);
  const [collections, setCollections] = useState<OrbitCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = () => setTick((t) => t + 1);

  useEffect(() => {
    if (!clientId) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const list = await aaiApi.listOrbitCollections();
        if (live) setCollections(list.data.collections);
        const latest = list.data.collections[0];
        if (!latest) {
          if (live) setView(null);
          return;
        }
        const full = await aaiApi.getOrbitCollection(latest.id);
        if (live) setView(full.data);
      } catch {
        if (live) setError('Could not load the Orbit collections.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [clientId, tick]);

  return { view, collections, loading, error, reload };
};

export const ClientContextBody: React.FC<{
  viewer: ContextViewer;
  clientId: string;
  clientName: string;
  allowed: WhatsAllowedData;
  cycle: { view: OrbitCollectionView | null; collections?: OrbitCollection[]; loading: boolean; error: string | null; reload?: () => void };
  links?: ActivityLinkResolver;
  beforeSources?: React.ReactNode;
  /** The platform controls, for the viewer who owns them. Given, it becomes the
   *  last tab; withheld, the tab does not exist. */
  controls?: React.ReactNode;
  /** The roster: the head's team, or everyone for a viewer with the whole company. */
  people?: React.ReactNode;
  /** The Orbit Room at the top. Off for the roles whose dashboard already carries it. */
  room?: boolean;
}> = ({ viewer, clientId, clientName, allowed, cycle, links, beforeSources, controls, people, room = true }) => {
  /*
   * A department head reads one department. Company tools are the company's
   * record and not theirs: those are the grants on the company's sources. What
   * is theirs is the department dossier above and their own activity, so those
   * are the whole screen.
   */
  let tabs = [...TABS];
  /* Company tools and Activity are commented out for the partner, the Owner,
     the AI Champion and the department head for now. The head's Your team tab
     goes with them; the Owner keeps People and Controls. */
  if (viewer === 'partner' || viewer === 'owner' || viewer === 'champion' || viewer === 'department') tabs = [];
  /*
  if (viewer === 'department') {
    tabs = [];
    if (people) tabs.push({ id: 'people', label: 'Your team' });
    tabs.push(...TABS.filter((t) => t.id === 'activity'));
  }
  */
  if (viewer !== 'department' && people) tabs.push({ id: 'people', label: 'People' });
  if (controls) tabs.push({ id: 'controls', label: 'Controls' });
  // Plenty of the app links here with ?tab=. 'overview' and 'findings' were
  // real tabs once, so a stale link lands on the first one that still exists
  // rather than on nothing.
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') || '';
  let tab = tabs[0]?.id || '';
  if (tabs.some((t) => t.id === requested)) tab = requested;
  if (requested === 'apps' && tabs.some((t) => t.id === 'sources')) tab = 'sources';
  const setTab = (id: string) => setParams({ tab: id }, { replace: true });
  let activityScope: 'company' | 'department' | 'mine' = 'company';
  if (viewer === 'department') activityScope = 'department';
  if (viewer === 'employee') activityScope = 'mine';

  return (
    <>
      {room && (
        <div className="mb-5">
          <OrbitRoomPanel viewer={viewer} clientId={clientId} />
        </div>
      )}

      {tabs.length > 1 && (
      <div id="context-tabs" role="tablist" aria-label="Context view" className="mb-5 flex overflow-x-auto rounded-panel border border-line bg-canvas-inset">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={clsx(
              'min-h-[56px] flex-1 whitespace-nowrap px-5 text-left text-[13px] font-semibold transition-colors',
              tab === item.id && 'bg-white text-ink shadow-[inset_0_-2px_#18191D]',
              tab !== item.id && 'text-ink-muted hover:text-ink',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>
      )}

      {tab === 'sources' && <AppsSourcesTab allowed={allowed} view={cycle.view} viewer={viewer} before={beforeSources} />}
      {tab === 'activity' && <ActivityView clientId={clientId} clientName={clientName} links={links} scope={activityScope} />}
      {tab === 'people' && people}
      {tab === 'controls' && controls}
    </>
  );
};

const ClientContextPage: React.FC = () => {
  const { active, loading: clientsLoading } = useActiveClient();

  const allowed = useAsync<WhatsAllowedData>(
    () => aaiApi.getWhatsAllowed(), 'Could not load this client’s context.', [active?.id],
  );
  const cycle = useLatestCollection(active?.id);

  if (clientsLoading) return <PageBody><Loading label="Loading your clients" /></PageBody>;
  if (!active) return <PageBody><ErrorNote message="Select an assigned client to see its context." /></PageBody>;
  if (allowed.loading) return <PageBody><Loading label="Loading client context" /></PageBody>;
  if (allowed.error || !allowed.data) {
    return <PageBody><ErrorNote message={allowed.error || 'Something went wrong.'} /></PageBody>;
  }

  return (
    <PageBody>
      <ClientHeader client={active} tabs={false} shared={false} />
      <ClientContextBody
        viewer="partner"
        clientId={active.id}
        clientName={active.name}
        allowed={allowed.data}
        cycle={cycle}
        room={false}
      />
    </PageBody>
  );
};

export default ClientContextPage;
